import { useRef, useSyncExternalStore } from 'react';
import { DomainStore } from '~/domain/changes/DomainStore';
import { domainTrace } from '~/domain/diagnostics/domainTrace';
import { Patient } from '~/domain/patient/patient';
import { PatientSession } from '~/domain/session/PatientSession';

interface CacheEntry<T> {
    /**
     * Each session owns its own store, and a newly loaded patient starts
     * at version 0, so the version alone does not tell two patients apart.
     */
    store: DomainStore;
    version: number;
    key: readonly unknown[];
    value: T;
}

/** Stable no-op subscribe used when there is no active session. */
const NOOP_SUBSCRIBE = (): (() => void) => () => {};

function sameKey(a: readonly unknown[], b: readonly unknown[]): boolean {
    return a.length === b.length && a.every((v, i) => Object.is(v, b[i]));
}

/**
 * Subscribes to the session's domain store and returns `compute(patient)`,
 * recomputed only when the store version, the store itself or an element
 * of `key` changes. Between those the same reference is returned, as
 * `useSyncExternalStore` requires. Returns `fallback` while `session` is
 * null.
 *
 * `key` must hold every input `compute` reads besides the patient, and
 * `fallback` must be a stable reference.
 */
export function useDomainProjection<T>(
    session: PatientSession | null,
    name: string,
    key: readonly unknown[],
    compute: (patient: Patient) => T,
    fallback: T
): T {
    const store = session?.context.store;
    const cacheRef = useRef<CacheEntry<T> | null>(null);

    return useSyncExternalStore(store?.subscribe ?? NOOP_SUBSCRIBE, () => {
        if (!session || !store) {
            cacheRef.current = null;
            return fallback;
        }
        const version = store.getVersion();
        const cached = cacheRef.current;
        if (
            cached &&
            cached.store === store &&
            cached.version === version &&
            sameKey(cached.key, key)
        ) {
            return cached.value;
        }
        const value = compute(session.patient);
        domainTrace.view(`${name} miss`, { version });
        cacheRef.current = { store, version, key, value };
        return value;
    });
}
